/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState } from 'react'
import { Alert } from 'antd'
import Text from '../../Text'
import Button from '../../Button'
import ProgressHeader from '../components/ProgressHeader'
import OptionCard from '../../OnboardingWizardModal/components/OptionCard'
import RegistrationIcon from '../icons/RegistrationIcon'
import ManualAddImportIcon from '../icons/ManualAddImportIcon'
import { useCompetitionSetup } from '../context/CompetitionSetupContext'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import { trackCompetitionFinish } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

/**
 * Registration Step component - Step 3 of the competition setup wizard
 * Allows users to choose between Registration or Manual Add/Import
 */
const RegistrationStep: React.FC = () => {
  const {
    state,
    setRegistrationType,
    buildSubmissionData,
    previousStep,
    skipWizard,
    completeWizard,
    onRefetch,
  } = useCompetitionSetup()
  const [localRegistrationType, setLocalRegistrationType] = useState<
    'registration' | 'manual' | null
  >(state.registrationType)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleBack = () => {
    // All structure types now have a step 2, so we can always go back normally
    previousStep()
  }

  const handleComplete = async () => {
    console.log(
      '🔄 RegistrationStep: Completing wizard with registration type:',
      localRegistrationType
    )

    if (!localRegistrationType) {
      console.warn('⚠️ RegistrationStep: No registration type selected')
      return
    }

    // Clear previous errors
    setError(null)

    // Save registration type to context
    setRegistrationType(localRegistrationType)

    // Build and log the final submission data
    const submissionData = buildSubmissionData(localRegistrationType)
    if (!submissionData) {
      console.warn('⚠️ RegistrationStep: Failed to build submission data')
      setError('Failed to prepare competition data. Please try again.')
      return
    }

    console.log('📤 Competition Setup Submission Data:', submissionData)

    // Track competition wizard completion with the chosen registration type
    trackCompetitionFinish(localRegistrationType)

    setLoading(true)
    try {
      const body = JSON.stringify(submissionData)
      const response = await req('/wizard/competition', {
        method: 'POST',
        body,
      })
      console.log('✅ Competition setup submitted successfully', response)

      // Refetch competition data to update the UI
      if (onRefetch) {
        console.log('🔄 RegistrationStep: Refreshing competition data')
        onRefetch()
      }

      if (localRegistrationType === 'registration') {
        // Redirect to registration page and open modal
        completeWizard('/registration?openRegistrationModal=true')
      } else if (localRegistrationType === 'manual') {
        // Redirect to setup page and open add team action
        completeWizard('/setup?openAddTeam=true')
      }
    } catch (e: any) {
      console.error('🚨 handleComplete failed:', e)
      setError(
        e?.message || 'Failed to submit competition setup. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <StepContainer>
      <ProgressHeader currentStep={3} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            How Should Teams and Players Join?
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Choose how you want to add teams and players
          </Text>
        </TitleSection>

        <OptionsContainer>
          <OptionCard
            id='registration'
            icon={
              <RegistrationIcon
                color={
                  localRegistrationType === 'registration'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_100
                }
                size={40}
              />
            }
            title='Registration'
            description='Automate sign-ups by having teams or players register and make payments online'
            isSelected={localRegistrationType === 'registration'}
            onClick={() => setLocalRegistrationType('registration')}
          />

          <OptionCard
            id='manual_add_import'
            icon={
              <ManualAddImportIcon
                color={
                  localRegistrationType === 'manual'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_100
                }
                size={40}
              />
            }
            title='Manual Add/Import'
            description='Invite Team Managers to add Players, or import them to teams or all rosters'
            isSelected={localRegistrationType === 'manual'}
            onClick={() => setLocalRegistrationType('manual')}
          />
        </OptionsContainer>

        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            style={{ marginTop: '8px' }}
          />
        )}

        <ActionsContainer>
          <div
            css={css`
              display: flex;
              gap: 16px;
              align-items: center;
              width: 100%;
            `}
          >
            <Button
              text='BACK'
              onClick={handleBack}
              variant='secondary'
              uppercase
              disabled={loading}
              style={css`
                flex: 1;
              `}
            />
            <Button
              text={loading ? 'SUBMITTING...' : 'FINISH'}
              onClick={handleComplete}
              variant='primary'
              uppercase
              disabled={!localRegistrationType || loading}
              style={css`
                flex: 1;
              `}
            />
          </div>
          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default RegistrationStep
